import {
  findBlockByBlockId,
  parseHTMLToBlocks,
  parseInstructionsHtml,
  replaceBlock,
} from "@app/lib/editor/instruction_blocks";
import { KNOWLEDGE_TAG_REGEX } from "@app/lib/editor/knowledge_node_constants";
import {
  BLOCK_ID_ATTRIBUTE,
  BLOCK_ID_UNIQUE_ID_NODE_TYPES,
  INSTRUCTIONS_ROOT_NODE_NAME,
  SKILL_NODE_TYPE,
  TOOL_NODE_TYPE,
} from "@app/lib/editor/node_constants";
import {
  postProcessMarkdown,
  preprocessMarkdownForEditor,
} from "@app/lib/editor/skill_instructions_preprocessing";
import { DustError } from "@app/lib/error";
import { generateShortBlockId } from "@app/lib/generate_short_block_id";
import { parseSkillReferenceTag } from "@app/lib/skills/format";
import { parseToolTag } from "@app/lib/tools/format";
import type { Result } from "@app/types/shared/result";
import { Err, Ok } from "@app/types/shared/result";
import { INSTRUCTIONS_ROOT_TARGET_BLOCK_ID } from "@app/types/suggestions/agent_suggestion";
import type { Extensions, JSONContent } from "@tiptap/core";
import type { MarkdownManager } from "@tiptap/markdown";
import type {
  DOMParser as ProseMirrorDOMParser,
  Node as ProseMirrorNode,
} from "@tiptap/pm/model";
import type { Transform } from "@tiptap/pm/transform";
import type { renderToHTMLString } from "@tiptap/static-renderer/pm/html-string";

// Which editor's schema converts the instructions. Agents and skills share the block model but
// only agent instructions hold `instructionBlock` sections, so each gets its own pipeline.
export type InstructionsSchema = "skill" | "agent";

export interface MarkdownPipeline {
  extensions: Extensions;
  markdownManager: MarkdownManager;
  renderToHTMLString: typeof renderToHTMLString;
  cheerio: typeof import("cheerio");
  // Stands in for the browser's `document`, which the schema's DOM parser needs to read HTML.
  document: Document;
  domParser: ProseMirrorDOMParser;
  createTransform: (doc: ProseMirrorNode) => Transform;
}

const NODE_TYPES_WITH_BLOCK_ID = new Set<string>([
  ...BLOCK_ID_UNIQUE_ID_NODE_TYPES,
  INSTRUCTIONS_ROOT_NODE_NAME,
]);

function prepareNodesForStaticRenderer(node: JSONContent): JSONContent {
  if (node.type === "text" && typeof node.text === "string") {
    return { ...node, text: node.text.replace(/\u200B/g, "") };
  }

  if (
    node.type === "rawMarkdownBlock" &&
    typeof node.attrs?.rawContent === "string"
  ) {
    return {
      ...node,
      attrs: {
        ...node.attrs,
        rawContent: node.attrs.rawContent.replace(/\u200B/g, ""),
      },
    };
  }

  if (node.content) {
    return {
      ...node,
      content: node.content.map(prepareNodesForStaticRenderer),
    };
  }

  return node;
}

function addBlockIds(node: JSONContent | undefined): void {
  if (!node?.type) {
    return;
  }

  if (NODE_TYPES_WITH_BLOCK_ID.has(node.type)) {
    node.attrs = { ...node.attrs };
    if (node.type === INSTRUCTIONS_ROOT_NODE_NAME) {
      node.attrs[BLOCK_ID_ATTRIBUTE] = INSTRUCTIONS_ROOT_TARGET_BLOCK_ID;
    } else if (node.attrs[BLOCK_ID_ATTRIBUTE] == null) {
      node.attrs[BLOCK_ID_ATTRIBUTE] = generateShortBlockId();
    }
  }

  node.content?.forEach(addBlockIds);
}

/**
 * Strip presentation attributes from HTML so it is not permanently stored.
 */
function stripPresentationAttributes(
  html: string,
  pipeline: MarkdownPipeline
): string {
  const $ = pipeline.cheerio.load(html, { xmlMode: false }, false);
  // Strip class from all elements except <code>. The codeBlock extension
  // stores the fenced-code language as class="language-typescript", which is
  // the round-trip mechanism for recovering the language on generateJSON
  $("[class]").not("code").removeAttr("class");
  $("[style]").removeAttr("style");
  // Must maintain id on custom inline elements for their parseHTML rules.
  $("[id]").not("knowledge, tool, skill, unavailable_skill").removeAttr("id");
  return $.html();
}

/**
 * TipTap's server-side parseHTMLToken has no DOM, so it converts block-level
 * HTML tokens (e.g. standalone <knowledge .../> or <tool .../> lines) to plain-
 * text paragraphs instead of calling generateJSON + parseHTML rules. Walk the
 * parsed JSON tree and restore any such nodes back to proper inline-node
 * JSONContent before rendering to HTML.
 */
function recoverCustomInlineNodes(node: JSONContent): JSONContent {
  if (node.type === "paragraph" && node.content?.length === 1) {
    const child = node.content[0];
    if (child.type === "text") {
      const text = (child.text ?? "").trim();
      const knowledgeMatch = KNOWLEDGE_TAG_REGEX.exec(text);
      if (knowledgeMatch) {
        const attrs = knowledgeMatch[1];
        const id = /id="([^"]+)"/.exec(attrs)?.[1];
        const title = /title="([^"]+)"/.exec(attrs)?.[1];
        if (id && title) {
          return {
            ...node,
            content: [
              {
                type: "knowledgeNode",
                attrs: {
                  selectedItems: [
                    {
                      nodeId: id,
                      label: title,
                      spaceId: /space="([^"]*)"/.exec(attrs)?.[1] ?? "",
                      dataSourceViewId: /dsv="([^"]*)"/.exec(attrs)?.[1] ?? "",
                      hasChildren: /hasChildren="true"/.test(attrs),
                    },
                  ],
                },
              },
            ],
          };
        }
      }

      const tool = parseToolTag(text);
      if (tool) {
        return {
          ...node,
          content: [
            {
              type: TOOL_NODE_TYPE,
              attrs: {
                mcpServerViewId: tool.id,
                toolIcon: tool.icon,
                toolName: tool.name,
              },
            },
          ],
        };
      }

      const skill = parseSkillReferenceTag(text);
      if (skill) {
        return {
          ...node,
          content: [
            {
              type: SKILL_NODE_TYPE,
              attrs: {
                skillId: skill.id,
                skillIcon: skill.icon,
                skillName: skill.name,
                skillUnavailable: skill.unavailable ?? false,
              },
            },
          ],
        };
      }
    }
  }
  if (node.content) {
    return { ...node, content: node.content.map(recoverCustomInlineNodes) };
  }
  return node;
}

/**
 * Convert Markdown to stored skill instructionsHtml.
 * Uses the same extension schema as the browser editor, then strips CSS class attrs.
 */
export function convertMarkdownToBlockHtml(
  markdown: string,
  pipeline: MarkdownPipeline
): string {
  const preprocessed = preprocessMarkdownForEditor(markdown);
  const parsedDoc = preprocessed.trim()
    ? pipeline.markdownManager.parse(preprocessed)
    : null;

  const rawContent = parsedDoc?.content ?? [{ type: "paragraph" }];

  const json: JSONContent = {
    type: "doc",
    content: [
      {
        type: INSTRUCTIONS_ROOT_NODE_NAME,
        content: rawContent.map(recoverCustomInlineNodes),
      },
    ],
  };

  addBlockIds(json);

  const rendered = pipeline.renderToHTMLString({
    content: prepareNodesForStaticRenderer(json),
    extensions: pipeline.extensions,
  });

  return stripPresentationAttributes(rendered, pipeline);
}

export interface AppliedSkillInstructions {
  instructions: string;
  instructionsHtml: string;
}

/**
 * Converts stored block HTML back to the markdown `instructions` field, the way the editor
 * serializes it. Counterpart of `convertMarkdownToBlockHtml`.
 */
export function convertBlockHtmlToMarkdown(
  instructionsHtml: string,
  pipeline: MarkdownPipeline
): string {
  const { document, domParser, markdownManager } = pipeline;
  const doc = parseInstructionsHtml(instructionsHtml, { document, domParser });
  return postProcessMarkdown(markdownManager.serialize(doc.toJSON())).trim();
}

export function applyInstructionEditsToHtml(
  instructionsHtml: string,
  edits: { targetBlockId: string; content: string }[],
  pipeline: MarkdownPipeline
): Result<AppliedSkillInstructions, DustError<"invalid_request_error">> {
  const {
    createTransform,
    document,
    domParser,
    extensions,
    markdownManager,
    renderToHTMLString,
  } = pipeline;

  let doc = parseInstructionsHtml(instructionsHtml, { document, domParser });

  for (const { targetBlockId, content } of edits) {
    // Ids are cleared because the model is asked for the block "including the wrapping tag", so the
    // content routinely repeats the target's `data-block-id`. The browser's `UniqueID` plugin
    // regenerates duplicates on the next transaction; nothing does that here, and duplicate ids
    // make later edits target an ambiguous block. `addBlockIds` mints the cleared ones fresh.
    const newBlocks = parseHTMLToBlocks(content, targetBlockId, {
      clearBlockIds: true,
      document,
      domParser,
    });
    if (newBlocks.length === 0) {
      return new Err(
        new DustError(
          "invalid_request_error",
          `The edit targeting "${targetBlockId}" has no content.`
        )
      );
    }

    const target = findBlockByBlockId(doc, targetBlockId);
    if (!target) {
      return new Err(
        new DustError(
          "invalid_request_error",
          `The instructions no longer contain the block "${targetBlockId}" this edit targets.`
        )
      );
    }

    const tr = createTransform(doc);
    replaceBlock(tr, target, newBlocks);

    doc = tr.doc;
  }

  // The browser assigns ids through a ProseMirror plugin, which never runs without an editor.
  const json: JSONContent = doc.toJSON();
  addBlockIds(json);

  return new Ok({
    instructions: postProcessMarkdown(markdownManager.serialize(json)).trim(),
    instructionsHtml: stripPresentationAttributes(
      renderToHTMLString({
        content: prepareNodesForStaticRenderer(json),
        extensions,
      }),
      pipeline
    ),
  });
}
